import { useState } from 'react';
import { Check, CaretDown, MapPin } from '@phosphor-icons/react';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import {
  SUPPORTED_LOCATIONS, getCitiesForCountry, getCountryByName,
} from '@/config/locations';

/**
 * LocationPicker — controlled searchable country + city selectors.
 *
 * Props:
 *   value     : { country: string, city: string }
 *   onChange  : (next: { country, city }) => void
 *   label     : "From" | "To" | "Origin" | ...
 *   testIdPrefix : data-testid prefix for country/city triggers
 *   compact   : boolean — compact styling for in-card use (hero search)
 *   disabled  : boolean
 *
 * Behaviour:
 *   - Country dropdown shows flag + name, with search.
 *   - City dropdown is disabled until country is selected, then filters to that country's cities.
 *   - Changing country clears the city.
 *   - Free-text entry is impossible — only values from SUPPORTED_LOCATIONS are accepted.
 *   - Keyboard navigation via cmdk; mobile-friendly via shadcn Popover.
 */
const LocationPicker = ({
  value = { country: '', city: '' },
  onChange,
  label,
  testIdPrefix = 'loc',
  compact = false,
  disabled = false,
  mode = 'dual', // 'dual' (country+city side-by-side) | 'flat' (single combined trigger)
}) => {
  const [countryOpen, setCountryOpen] = useState(false);
  const [cityOpen, setCityOpen] = useState(false);
  const [flatOpen, setFlatOpen] = useState(false);

  const selectedCountry = getCountryByName(value.country);
  const cities = selectedCountry ? getCitiesForCountry(value.country) : [];
  const selectedCityLabel = cities.find((c) => c.value === value.city)?.label || '';

  const triggerCls = compact
    ? 'flex items-center justify-between w-full pl-10 pr-3 py-3 border border-gray-200 rounded-lg text-sm bg-white hover:border-gray-300 focus:outline-none focus:border-[#6D28D9] focus:ring-2 focus:ring-purple-100 transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed'
    : 'flex items-center justify-between w-full px-3.5 py-3 border border-gray-200 rounded-lg text-sm bg-white hover:border-gray-300 focus:outline-none focus:border-[#6D28D9] focus:ring-2 focus:ring-purple-100 transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed';

  // FLAT mode — single trigger, popover lists every "City, Country" combo
  if (mode === 'flat') {
    const labelText = selectedCountry && selectedCityLabel
      ? `${selectedCountry.flag} ${selectedCityLabel}, ${selectedCountry.name}`
      : (selectedCountry ? `${selectedCountry.flag} ${selectedCountry.name}` : 'Select location');

    return (
      <Popover open={flatOpen} onOpenChange={setFlatOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            role="combobox"
            aria-expanded={flatOpen}
            disabled={disabled}
            className={`relative ${triggerCls}`}
            data-testid={`${testIdPrefix}-flat-trigger`}
          >
            {compact && <MapPin size={18} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />}
            <span className={`truncate ${selectedCountry ? 'text-gray-900' : 'text-gray-400'}`}>{labelText}</span>
            <CaretDown size={14} weight="bold" className="text-gray-400 flex-shrink-0 ml-2" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[280px]" align="start">
          <Command>
            <CommandInput placeholder="Search city or country…" data-testid={`${testIdPrefix}-flat-search`} />
            <CommandList>
              <CommandEmpty>No location found.</CommandEmpty>
              {SUPPORTED_LOCATIONS.map((c) => (
                <CommandGroup key={c.code} heading={`${c.flag} ${c.name}`}>
                  {c.cities.map((city) => (
                    <CommandItem
                      key={`${c.code}-${city.value}`}
                      value={`${city.label} ${c.name}`}
                      onSelect={() => {
                        onChange?.({ country: c.name, city: city.value });
                        setFlatOpen(false);
                      }}
                      data-testid={`${testIdPrefix}-flat-option-${c.code}-${city.value}`}
                    >
                      <span className="flex-1">{city.label}</span>
                      {value.country === c.name && value.city === city.value && (
                        <Check size={16} weight="bold" className="text-[#6D28D9]" />
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-3" data-testid={`${testIdPrefix}-picker`}>
      {/* Country */}
      <div>
        {label && !compact && (
          <label className="block text-xs font-semibold text-gray-600 mb-2">{label} · Country</label>
        )}
        {compact && label && (
          <label className="block text-xs font-semibold text-gray-600 mb-2">{label}</label>
        )}
        <Popover open={countryOpen} onOpenChange={setCountryOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              role="combobox"
              aria-expanded={countryOpen}
              disabled={disabled}
              className={`relative ${triggerCls}`}
              data-testid={`${testIdPrefix}-country-trigger`}
            >
              {compact && <MapPin size={18} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />}
              <span className={`truncate ${selectedCountry ? 'text-gray-900' : 'text-gray-400'}`}>
                {selectedCountry ? (
                  <>
                    <span className="mr-1.5" aria-hidden>{selectedCountry.flag}</span>
                    {selectedCountry.name}
                  </>
                ) : 'Select country'}
              </span>
              <CaretDown size={14} weight="bold" className="text-gray-400 flex-shrink-0 ml-2" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="p-0 w-[260px]" align="start">
            <Command>
              <CommandInput placeholder="Search country…" data-testid={`${testIdPrefix}-country-search`} />
              <CommandList>
                <CommandEmpty>No country found.</CommandEmpty>
                <CommandGroup>
                  {SUPPORTED_LOCATIONS.map((c) => (
                    <CommandItem
                      key={c.code}
                      value={c.name}
                      onSelect={() => {
                        // Switching country clears the city
                        onChange?.({ country: c.name, city: value.country === c.name ? value.city : '' });
                        setCountryOpen(false);
                      }}
                      data-testid={`${testIdPrefix}-country-option-${c.code}`}
                    >
                      <span className="mr-2" aria-hidden>{c.flag}</span>
                      <span className="flex-1">{c.name}</span>
                      {value.country === c.name && <Check size={16} weight="bold" className="text-[#6D28D9]" />}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* City */}
      <div>
        {label && !compact && (
          <label className="block text-xs font-semibold text-gray-600 mb-2">{label} · City</label>
        )}
        {compact && <label className="block text-xs font-semibold text-gray-600 mb-2">City</label>}
        <Popover open={cityOpen} onOpenChange={(o) => selectedCountry && setCityOpen(o)}>
          <PopoverTrigger asChild>
            <button
              type="button"
              role="combobox"
              aria-expanded={cityOpen}
              disabled={disabled || !selectedCountry}
              className={`relative ${triggerCls}`}
              data-testid={`${testIdPrefix}-city-trigger`}
            >
              {compact && <MapPin size={18} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />}
              <span className={`truncate ${selectedCityLabel ? 'text-gray-900' : 'text-gray-400'}`}>
                {selectedCityLabel || (selectedCountry ? 'Select city' : 'Pick country first')}
              </span>
              <CaretDown size={14} weight="bold" className="text-gray-400 flex-shrink-0 ml-2" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="p-0 w-[260px]" align="start">
            <Command>
              <CommandInput placeholder="Search city…" data-testid={`${testIdPrefix}-city-search`} />
              <CommandList>
                <CommandEmpty>No city found.</CommandEmpty>
                <CommandGroup>
                  {cities.map((city) => (
                    <CommandItem
                      key={city.value}
                      value={city.label}
                      onSelect={() => {
                        onChange?.({ country: value.country, city: city.value });
                        setCityOpen(false);
                      }}
                      data-testid={`${testIdPrefix}-city-option-${city.value}`}
                    >
                      <span className="flex-1">{city.label}</span>
                      {value.city === city.value && <Check size={16} weight="bold" className="text-[#6D28D9]" />}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

export default LocationPicker;
